/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description File Secondary Claim button
 */
import _ from 'lodash';
import { Modal, Form, Select, Input, type ButtonProps, Space, Flex, Result, Typography, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Plus } from '@icon-park/react';

import NO_DATA_FOUND from 'assets/custom-no-data.svg';
import Button from 'components/widgets/button';
import { getClaimsReviewSecondaryClaimPath } from 'utils/router';
import { useClaimFilingStoreActions } from 'store/claimFiling';
import useGetSecondaryPayers from 'hooks/claim/useGetSecondaryPayers';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';

interface ISecondaryClaimModalProps {
	loading: boolean;
	visible: boolean;
	secondaryPayers: ClaimRequest.ISecondaryClaimPayers[];
	patientId: string;
	onCancel: VoidFunction;
	onProceed: VoidFunction;
	tenantPayers: Insurance.ITenantPayerOption[];
	refetch: VoidFunction;
}

const StepIndicator: React.FC<{ currentStep: number; totalSteps: number }> = ({ currentStep, totalSteps }) => (
	<span
		style={{
			fontSize: '14px',
			color: '#666',
			fontWeight: 500,
			marginLeft: 'auto',
		}}
	>
		Step {currentStep}/{totalSteps}
	</span>
);

const { Text, Title } = Typography;

const SecondaryClaimModal: React.FC<ISecondaryClaimModalProps> = ({
	patientId,
	visible,
	loading,
	refetch,
	secondaryPayers,
	onCancel,
	onProceed,
	tenantPayers,
}) => {
	const [form] = Form.useForm();
	const [selectedInsurance, setSelectedInsurance] = useState<ClaimRequest.ISecondaryClaimPayers>(null);
	const [selectedCoverageId, setSelectedCoverageId] = useState<string>(null);
	const { setPayerInfo, setSubscriberInfo, setCoverageInfo } = useClaimFilingStoreActions();

	const handlePayerChange = (coverageId: string) => {
		const selectedPayer = _.find(secondaryPayers, (item) => item?.coverage?.id === coverageId);
		const memberId = selectedPayer?.coverage?.subscriberId?.[0]?.value;

		if (selectedPayer) {
			const givenName = selectedPayer?.subscriber?.name?.[0]?.given?.[0] ?? '';
			const familyName = selectedPayer?.subscriber?.name?.[0]?.family ?? '';
			const subscriberName = _.compact([givenName, familyName]).join(' ');

			form.setFieldsValue({
				subscriberName,
				memberId: memberId || '',
			});
			setSelectedInsurance(selectedPayer);
		}
		setPayerInfo(selectedPayer?.payer as Fhir.Organization);
		setSubscriberInfo(selectedPayer?.subscriber as Fhir.Patient);
		setCoverageInfo(selectedPayer?.coverage as Fhir.Coverage);
	};

	useEffect(() => {
		if (selectedCoverageId && !_.isEmpty(secondaryPayers)) {
			const payer = _.find(secondaryPayers, (item) => item?.coverage?.id === selectedCoverageId);
			if (payer) {
				form.setFieldsValue({ payerName: payer.coverage?.id });
				handlePayerChange(payer.coverage?.id);
				setSelectedCoverageId(null);
			}
		}
	}, [secondaryPayers, selectedCoverageId]);

	const selectOptions = _.map(secondaryPayers, (item) => {
		const payerName = item?.payer?.name || 'Unknown Payer';
		const memberId = item?.coverage?.subscriberId?.[0]?.value || '';
		const givenName = item?.subscriber?.name?.[0]?.given?.[0] ?? '';
		const familyName = item?.subscriber?.name?.[0]?.family ?? '';
		const subscriberName = _.compact([givenName, familyName]).join(' ');
		const isDisabled = item?.primary;
		const responsibilitySequence = _.find(
			item?.coverage?.extension,
			(ext) => ext?.url === fhirSystemMapping.responsibilitySequence,
		)?.valueString;

		return {
			key: item?.coverage?.id,
			value: item?.coverage?.id,
			disabled: isDisabled,
			label: (
				<Flex vertical className='py-2' style={{ opacity: isDisabled ? 0.4 : 1 }}>
					<Title level={5} className='m-0'>
						{payerName}
					</Title>
					<Text type='secondary'>
						{_.map(
							_.compact([
								memberId ? { label: 'Member ID', value: memberId } : null,
								subscriberName ? { label: 'Subscriber', value: subscriberName } : null,
								responsibilitySequence
									? {
											label: 'Responsibility Sequence',
											value: responsibilitySequence,
										}
									: null,
							]),
							(meta, idx) => (
								<span key={`payer-meta-${idx}`}>
									{idx !== 0 && <Divider className='h-3 border-[#D4E6FF]' type='vertical' />}
									{meta.label}: <span className='font-medium'>{meta.value}</span>
								</span>
							),
						)}
					</Text>
				</Flex>
			),
			triggerLabel: payerName,
			searchText: `${payerName} ${memberId}`.toLowerCase(),
		};
	});

	const handleReset = () => {
		form.resetFields();
		setSelectedInsurance(null);
	};

	const handleProceed = () => {
		form.validateFields().then(() => {
			onProceed();
		});
	};

	return (
		<Modal
			loading={loading}
			title={
				<Space>
					<span>Choose the Secondary Insurance</span>
					<StepIndicator currentStep={1} totalSteps={2} />
				</Space>
			}
			open={visible}
			onCancel={onCancel}
			width={670}
			footer={[
				<Button key='reset' onClick={handleReset}>
					Reset
				</Button>,
				<Button key='proceed' type='primary' disabled={_.isEmpty(secondaryPayers)} onClick={handleProceed}>
					Proceed
				</Button>,
			]}
			destroyOnHidden
			maskClosable={false}
			zIndex={1000}
		>
			{_.isEmpty(secondaryPayers) ? (
				<Result
					icon={<img src={NO_DATA_FOUND} alt='no-data' width={150} height={150} />}
					subTitle='No insurance policies found'
					extra={
						<Button.AddInsurance
							existingPayerResponsibility={[]}
							tenantPayers={tenantPayers}
							patientId={patientId}
							title='Add New Insurance'
							refetchSecondaryPayers={refetch}
							onInsuranceCreation={(newCoverageId) => setSelectedCoverageId(newCoverageId)}
						/>
					}
				/>
			) : (
				<Form form={form} layout='vertical'>
					<Form.Item label='Insurance Name' name='payerName' rules={[{ required: true, message: 'Please select a payer' }]}>
						<Select
							showSearch
							placeholder='Search by Insurance name or Member ID'
							value={selectedInsurance?.coverage?.id}
							options={selectOptions}
							onChange={handlePayerChange}
							filterOption={(input, option) => option?.searchText?.includes(input?.toLowerCase())}
							optionLabelProp='triggerLabel'
							style={{ width: '100%' }}
							popupRender={(menu) => (
								<>
									{menu}
									<Divider className='my-2' />
									<Button.AddInsurance
										type='link'
										icon={<Plus size={14} />}
										existingPayerResponsibility={[]}
										tenantPayers={tenantPayers}
										patientId={patientId}
										title='Add New Insurance'
										refetchSecondaryPayers={refetch}
										onInsuranceCreation={(newCoverageId) => setSelectedCoverageId(newCoverageId)}
									/>
								</>
							)}
						/>
					</Form.Item>

					<Flex gap={16}>
						<Form.Item label='Subscriber Name' name='subscriberName' style={{ flex: 1 }}>
							<Input disabled placeholder='E.g. John Doe' />
						</Form.Item>

						<Form.Item label='Member ID' name='memberId' style={{ flex: 1 }}>
							<Input disabled placeholder='E.g. WX1234FJCHI' />
						</Form.Item>
					</Flex>
				</Form>
			)}
		</Modal>
	);
};

const FileSecondaryClaim: React.FC<
	ButtonProps & { claimId: string; patientId?: string; tenantPayers?: Insurance.ITenantPayerOption[] }
> = ({ claimId, patientId, tenantPayers, ...props }) => {
	const { data: secondaryPayers, isLoading, refetch, isFetching } = useGetSecondaryPayers(claimId);
	const [modalVisible, setModalVisible] = useState(false);
	const navigate = useNavigate();
	const { resetState } = useClaimFilingStoreActions();

	const handleClick = () => {
		resetState();
		setModalVisible(true);
	};

	const handleCancel = () => {
		setModalVisible(false);
	};

	const handleProceed = () => {
		setModalVisible(false);
		navigate(getClaimsReviewSecondaryClaimPath(claimId));
	};

	return (
		<>
			<Button type='primary' onClick={handleClick} {...props}>
				{'File Secondary'}
			</Button>

			<SecondaryClaimModal
				loading={isLoading || isFetching}
				refetch={refetch}
				visible={modalVisible}
				patientId={patientId}
				secondaryPayers={secondaryPayers}
				onCancel={handleCancel}
				onProceed={handleProceed}
				tenantPayers={tenantPayers}
			/>
		</>
	);
};

export default FileSecondaryClaim;
export { StepIndicator };
